import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios, { fetchCsrfToken } from '../../utils/axiosConfig';
import API_URL from '../../config';
import SEO from '../SEO';
import toast from 'react-hot-toast';
import debounce from 'lodash/debounce';

// 創建一個全局的快取物件
const settingsCache = {};

// Twitter Card 類型說明
const TWITTER_CARD_DESCRIPTIONS = {
  summary: {
    title: '摘要卡片',
    description: '適用於大多數網頁。顯示標題、簡短描述和小型縮略圖。',
    dimensions: '圖片尺寸建議：120x120px',
    example: '/images/twitter-card-summary.png'
  },
  summary_large_image: {
    title: '大圖卡片',
    description: '適用於圖片為主的內容，如文章或產品。顯示大型橫幅圖片。',
    dimensions: '圖片尺寸建議：1200x630px',
    example: '/images/twitter-card-large.png'
  },
  app: {
    title: 'App 卡片',
    description: '專為行動應用設計。可顯示應用名稱、描述和下載連結。',
    dimensions: '圖片尺寸建議：800x800px',
    example: '/images/twitter-card-app.png'
  },
  player: {
    title: '播放器卡片',
    description: '適用於視頻或音頻內容。可直接在 Twitter 上播放媒體。',
    dimensions: '視頻尺寸建議：1280x720px',
    example: '/images/twitter-card-player.png'
  }
};

const SeoManager = () => {
  const [settings, setSettings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSetting, setCurrentSetting] = useState({
    pagePath: '',
    title: '',
    description: '',
    keywords: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    twitterCard: 'summary_large_image',
    canonicalUrl: '',
    robots: 'index, follow',
    enableDynamicFill: false
  });
  const [editMode, setEditMode] = useState(false);
  const [characterCounts, setCharacterCounts] = useState({
    title: 0,
    description: 0,
    ogTitle: 0,
    ogDescription: 0,
    twitterTitle: 0,
    twitterDescription: 0
  });
  const [showCardPreview, setShowCardPreview] = useState(false);

  const MAX_LENGTHS = {
    title: 120,
    description: 160,
    ogTitle: 120,
    ogDescription: 160,
    twitterTitle: 120,
    twitterDescription: 100,
    keywords: 200
  };

  const fetchSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('開始獲取 SEO 設定...');

      const response = await axios.get(`${API_URL}/api/seo-settings`);
      console.log('獲取到的 SEO 設定:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.error?.message || '獲取 SEO 設定失敗');
      }

      // 處理數據，確保每個設定都有正確的 path
      const processedSettings = response.data.data.map(setting => {
        // 如果沒有 path，嘗試從其他欄位獲取
        const path = setting.pagePath || '';
        
        // 確保路徑格式正確
        const formattedPath = path.startsWith('/') ? path : `/${path}`;
        
        return {
          ...setting,
          pagePath: formattedPath
        };
      });

      // 更新快取和狀態
      const sortedSettings = processedSettings.sort((a, b) => 
        new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
      );
      
      console.log('處理後的設定:', sortedSettings);
      
      settingsCache.data = sortedSettings;
      settingsCache.timestamp = Date.now();
      
      setSettings(sortedSettings);
    } catch (error) {
      console.error('獲取 SEO 設定時發生錯誤:', error);
      setError(error.response?.data?.error?.message || '載入 SEO 設定失敗');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 添加快取過期檢查
  useEffect(() => {
    const checkCacheExpiry = () => {
      if (settingsCache.timestamp) {
        const cacheAge = Date.now() - settingsCache.timestamp;
        // 如果快取超過 5 分鐘，清除快取
        if (cacheAge > 5 * 60 * 1000) {
          delete settingsCache.data;
          delete settingsCache.timestamp;
        }
      }
    };

    // 每分鐘檢查一次快取是否過期
    const interval = setInterval(checkCacheExpiry, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        // 確保有 CSRF token
        await fetchCsrfToken();
        await fetchSettings();
      } catch (error) {
        console.error('初始化失敗:', error);
        setError('初始化失敗，請重新整理頁面');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [fetchSettings]);

  const handleOpenModal = (setting = null) => {
    if (setting) {
      // 編輯模式：使用現有設定
      const formattedPath = setting.pagePath?.startsWith('/') ? setting.pagePath : `/${setting.pagePath || ''}`;
      setCurrentSetting({
        pagePath: formattedPath,
        title: setting.title || '',
        description: setting.description || '',
        keywords: setting.keywords || '',
        ogTitle: setting.ogTitle || '',
        ogDescription: setting.ogDescription || '',
        ogImage: setting.ogImage || '',
        twitterTitle: setting.twitterTitle || '',
        twitterDescription: setting.twitterDescription || '',
        twitterImage: setting.twitterImage || '',
        twitterCard: setting.twitterCard || 'summary_large_image',
        canonicalUrl: setting.canonicalUrl || '',
        robots: setting.robots || 'index, follow',
        enableDynamicFill: setting.enableDynamicFill || false,
        _id: setting._id
      });
      setEditMode(true);
    } else {
      // 新增模式：使用預設值
      setCurrentSetting({
        pagePath: '/articles/', // 預設路徑模板
        title: '',
        description: '',
        keywords: '',
        ogTitle: '',
        ogDescription: '',
        ogImage: '',
        twitterTitle: '',
        twitterDescription: '',
        twitterImage: '',
        twitterCard: 'summary_large_image',
        canonicalUrl: '',
        robots: 'index, follow',
        enableDynamicFill: false
      });
      setEditMode(false);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentSetting({
      pagePath: '/articles/',  // 重置時也使用預設路徑模板
      title: '',
      description: '',
      keywords: '',
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
      twitterCard: 'summary_large_image',
      canonicalUrl: '',
      robots: 'index, follow',
      enableDynamicFill: false
    });
    setEditMode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 顯示載入中的 toast
    const loadingToast = toast.loading('正在儲存 SEO 設定...');
    
    try {
      setError(null);
      console.log('開始提交 SEO 設定...');
      
      // 檢查基本必填欄位
      const requiredFields = [
        { field: 'pagePath', label: '路徑' },
        { field: 'title', label: '標題' },
        { field: 'description', label: '描述' }
      ];
      
      const emptyFields = requiredFields.filter(({ field }) => !currentSetting[field]?.trim());
      
      if (emptyFields.length > 0) {
        const errorMsg = `請填寫以下必填欄位：${emptyFields.map(f => f.label).join(', ')}`;
        setError(errorMsg);
        toast.error(errorMsg, { id: loadingToast });
        return;
      }

      // 處理路徑格式
      let pagePath = currentSetting.pagePath.trim();
      // 移除開頭的 http://localhost:3000 或 domain 如果存在
      pagePath = pagePath.replace(/^https?:\/\/[^/]+/, '');
      
      // 移除多餘的斜線並確保格式正確
      pagePath = pagePath.replace(/\/+/g, '/');
      pagePath = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
      pagePath = pagePath.endsWith('/') ? pagePath.slice(0, -1) : pagePath;

      // 檢查路徑長度
      if (pagePath.length < 2) {
        const errorMsg = '路徑長度必須至少為 2 個字元';
        setError(errorMsg);
        toast.error(errorMsg, { id: loadingToast });
        return;
      }

      // 準備要發送的數據
      const settingData = {
        pagePath: currentSetting.pagePath.trim(),
        title: currentSetting.title.trim(),
        description: currentSetting.description.trim(),
        keywords: currentSetting.keywords ? currentSetting.keywords.trim() : '',
        ogTitle: currentSetting.ogTitle ? currentSetting.ogTitle.trim() : currentSetting.title.trim(),
        ogDescription: currentSetting.ogDescription ? currentSetting.ogDescription.trim() : currentSetting.description.trim(),
        ogImage: currentSetting.ogImage ? currentSetting.ogImage.trim() : '/images/og-default.jpg',
        twitterTitle: currentSetting.twitterTitle ? currentSetting.twitterTitle.trim() : currentSetting.ogTitle || currentSetting.title.trim(),
        twitterDescription: currentSetting.twitterDescription ? currentSetting.twitterDescription.trim() : currentSetting.ogDescription || currentSetting.description.trim(),
        twitterImage: currentSetting.twitterImage ? currentSetting.twitterImage.trim() : currentSetting.ogImage || '/images/og-default.jpg',
        twitterCard: currentSetting.twitterCard || 'summary_large_image',
        canonicalUrl: currentSetting.canonicalUrl ? currentSetting.canonicalUrl.trim() : `${window.location.origin}${currentSetting.pagePath}`,
        robots: currentSetting.robots || 'index, follow',
        enableDynamicFill: currentSetting.enableDynamicFill || false
      };

      console.log('準備發送的數據:', settingData);

      // 確保 CSRF token 已經獲取
      await fetchCsrfToken();
      
      let response;
      const apiEndpoint = `${API_URL}/api/seo-settings`;
      console.log('API 端點:', apiEndpoint);

      if (editMode && currentSetting._id) {
        response = await axios.put(`${apiEndpoint}/${currentSetting._id}`, settingData);
      } else {
        response = await axios.post(apiEndpoint, settingData);
      }

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '儲存 SEO 設定失敗');
      }

      // 清除快取並重新獲取設定
      delete settingsCache.data;
      delete settingsCache.timestamp;
      await fetchSettings();
      
      // 關閉模態框並顯示成功消息
      handleCloseModal();
      toast.success('SEO 設定已成功' + (editMode ? '更新' : '創建'), { id: loadingToast });
      
    } catch (error) {
      console.error('提交 SEO 設定時發生錯誤:', error);
      let errorMessage = error.response?.data?.error?.message || 
                        error.response?.data?.message || 
                        error.message || 
                        '發生未知錯誤';
      setError(errorMessage);
      toast.error(errorMessage, { id: loadingToast });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this SEO setting?')) {
      try {
        const response = await axios.delete(`${API_URL}/api/seo-settings/${id}`);
        
        if (!response.data.success) {
          throw new Error(response.data.error.message);
        }
        
        delete settingsCache.data;
        delete settingsCache.timestamp;
        await fetchSettings();
      } catch (error) {
        console.error('Error deleting SEO setting:', error);
        setError(error.response?.data?.error?.message || 'Failed to delete SEO setting');
      }
    }
  };

  // 更新字數計數
  const updateCharacterCounts = useCallback(debounce((field, value) => {
    if (value === undefined || value === null) return;
    
    setCharacterCounts(prev => ({
      ...prev,
      [field]: (value || '').length
    }));
  }, 300), []);

  // Add dynamic fill effect
  useEffect(() => {
    const fetchDynamicFillData = async () => {
      if (!currentSetting.enableDynamicFill || !currentSetting.pagePath) return;
      
      try {
        const response = await axios.get(`${API_URL}/api/seo-settings/preview${currentSetting.pagePath}`);
        if (response.data.success) {
          const { title, description, ogTitle, ogDescription, twitterTitle, twitterDescription } = response.data.data;
          
          setCurrentSetting(prev => ({
            ...prev,
            title: prev.title || title || '',
            description: prev.description || description || '',
            ogTitle: prev.ogTitle || ogTitle || '',
            ogDescription: prev.ogDescription || ogDescription || '',
            twitterTitle: prev.twitterTitle || twitterTitle || '',
            twitterDescription: prev.twitterDescription || twitterDescription || '',
          }));

          // Update character counts with safe values
          updateCharacterCounts('title', title || '');
          updateCharacterCounts('description', description || '');
          updateCharacterCounts('ogTitle', ogTitle || '');
          updateCharacterCounts('ogDescription', ogDescription || '');
          updateCharacterCounts('twitterTitle', twitterTitle || '');
          updateCharacterCounts('twitterDescription', twitterDescription || '');

          toast.success('已載入動態填充數據');
        }
      } catch (error) {
        console.error('[SeoManager] 載入動態填充數據失敗:', error);
        toast.error('無法載入動態填充數據');
      }
    };

    fetchDynamicFillData();
  }, [currentSetting.enableDynamicFill, currentSetting.pagePath]);

  // 自動填充處理
  const handleAutoFill = (sourceField, targetFields) => {
    const sourceValue = currentSetting[sourceField] || '';
    if (!sourceValue) return;

    const updates = {};
    targetFields.forEach(field => {
      // 直接覆蓋現有值
      updates[field] = sourceValue.slice(0, MAX_LENGTHS[field] || sourceValue.length);
    });

    if (Object.keys(updates).length > 0) {
      setCurrentSetting(prev => ({
        ...prev,
        ...updates
      }));

      // 更新字數計數
      Object.keys(updates).forEach(field => {
        updateCharacterCounts(field, updates[field] || '');
      });

      toast.success('已自動填充相關欄位');
    }
  };

  // 處理輸入變化
  const handleInputChange = (e) => {
    const { name, value = '' } = e.target;
    setCurrentSetting(prev => ({
      ...prev,
      [name]: value
    }));
    updateCharacterCounts(name, value);
  };

  // 渲染表單欄位組
  const renderFieldGroup = (title, fields) => (
    <div className="space-y-4 border-b border-gray-200 pb-4 mb-4">
      <h4 className="text-lg font-medium text-gray-900">{title}</h4>
      {fields}
    </div>
  );

  // 渲染帶有字數計數的輸入框
  const renderInputWithCounter = (name, label, rows = 1, options = {}) => {
    const {
      showAutoFill = false,
      autoFillSource = 'title',
      autoFillFields = [],
      placeholder = ''
    } = options;

    return (
      <div>
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
          {showAutoFill && (
            <button
              type="button"
              onClick={() => handleAutoFill(autoFillSource, autoFillFields)}
              className="text-xs text-blue-500 hover:text-blue-700"
            >
              自動填充
            </button>
          )}
        </div>
        {rows > 1 ? (
          <textarea
            name={name}
            value={currentSetting[name]}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            rows={rows}
            maxLength={MAX_LENGTHS[name]}
            placeholder={placeholder}
          />
        ) : (
          <input
            type="text"
            name={name}
            value={currentSetting[name]}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            maxLength={MAX_LENGTHS[name]}
            placeholder={placeholder}
          />
        )}
        {MAX_LENGTHS[name] && (
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">
              {characterCounts[name] || 0}/{MAX_LENGTHS[name]} 字元
            </span>
            {characterCounts[name] > MAX_LENGTHS[name] * 0.9 && (
              <span className="text-xs text-yellow-500">
                接近字數限制
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Twitter Card 預覽組件
  const TwitterCardPreview = ({ type, data }) => {
    const cardInfo = TWITTER_CARD_DESCRIPTIONS[type];
    if (!cardInfo) return null;

    return (
      <div className="bg-white p-4 rounded-lg shadow-lg">
        <h3 className="text-lg font-bold mb-2">{cardInfo.title}</h3>
        <div className="mb-4">
          <p className="text-gray-600 text-sm mb-2">{cardInfo.description}</p>
          <p className="text-gray-500 text-xs">{cardInfo.dimensions}</p>
        </div>
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-1">預覽效果：</h4>
            <div className="bg-white border rounded-lg p-2">
              <div className="mb-2">
                <img
                  src={data.ogImage || cardInfo.example}
                  alt="Preview"
                  className="w-full h-auto rounded"
                />
              </div>
              <div className="text-sm">
                <p className="font-bold">{data.title || '標題示例'}</p>
                <p className="text-gray-600 text-xs mt-1">
                  {data.description || '描述示例'}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {window.location.hostname}
                </p>
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            <p>* 實際顯示效果可能因平台而異</p>
          </div>
        </div>
      </div>
    );
  };

  // 修改 Twitter Card 選擇器部分
  const renderTwitterCardSelector = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Twitter Card 類型
          <span 
            className="ml-2 text-xs text-blue-500 cursor-pointer hover:text-blue-700"
            onClick={() => setShowCardPreview(!showCardPreview)}
          >
            {showCardPreview ? '隱藏預覽' : '查看預覽'}
          </span>
        </label>
        <select
          name="twitterCard"
          value={currentSetting.twitterCard}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        >
          {Object.entries(TWITTER_CARD_DESCRIPTIONS).map(([value, { title }]) => (
            <option key={value} value={value}>
              {title}
            </option>
          ))}
        </select>
      </div>
      
      {showCardPreview && (
        <div className="mt-4">
          <TwitterCardPreview
            type={currentSetting.twitterCard}
            data={currentSetting}
          />
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 使用 SEO 組件設置當前頁面的 SEO */}
      <SEO path="/admin/seo-manager" />

      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">SEO Settings Manager</h1>
          <button
            onClick={() => handleOpenModal()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Add New Setting
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page Path</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {settings.map((setting) => (
                <tr key={setting._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {setting.pagePath || ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{setting.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{setting.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleOpenModal(setting)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(setting._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[800px] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {editMode ? '編輯 SEO 設定' : '新增 SEO 設定'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 基本設定 */}
                  {renderFieldGroup('基本設定', (
                    <>
                      {renderInputWithCounter('pagePath', '頁面路徑')}
                      {renderInputWithCounter('title', '標題')}
                      {renderInputWithCounter('description', '描述', 3)}
                      {renderInputWithCounter('keywords', '關鍵字')}
                    </>
                  ))}

                  {/* Open Graph 設定 */}
                  {renderFieldGroup('Open Graph 設定', (
                    <>
                      {renderInputWithCounter('ogTitle', 'OG 標題', 1, {
                        showAutoFill: true,
                        autoFillSource: 'title',
                        autoFillFields: ['ogTitle']
                      })}
                      {renderInputWithCounter('ogDescription', 'OG 描述', 3, {
                        showAutoFill: true,
                        autoFillSource: 'description',
                        autoFillFields: ['ogDescription']
                      })}
                      {renderInputWithCounter('ogImage', 'OG 圖片網址', 1, {
                        placeholder: '請輸入完整的圖片 URL'
                      })}
                    </>
                  ))}

                  {/* Twitter Card 設定 */}
                  {renderFieldGroup('Twitter Card 設定', (
                    <>
                      {renderTwitterCardSelector()}
                      {renderInputWithCounter('twitterTitle', 'Twitter 標題', 1, {
                        showAutoFill: true,
                        autoFillSource: 'ogTitle',
                        autoFillFields: ['twitterTitle']
                      })}
                      {renderInputWithCounter('twitterDescription', 'Twitter 描述', 3, {
                        showAutoFill: true,
                        autoFillSource: 'ogDescription',
                        autoFillFields: ['twitterDescription']
                      })}
                      {renderInputWithCounter('twitterImage', 'Twitter 圖片網址', 1, {
                        showAutoFill: true,
                        autoFillSource: 'ogImage',
                        autoFillFields: ['twitterImage'],
                        placeholder: '請輸入完整的圖片 URL'
                      })}
                    </>
                  ))}

                  {/* 其他設定 */}
                  {renderFieldGroup('其他設定', (
                    <>
                      {renderInputWithCounter('canonicalUrl', '規範連結')}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          搜尋引擎索引設定
                        </label>
                        <select
                          name="robots"
                          value={currentSetting.robots}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          <option value="index, follow">允許索引與跟隨連結</option>
                          <option value="noindex, nofollow">禁止索引與跟隨連結</option>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="enableDynamicFill"
                          checked={currentSetting.enableDynamicFill}
                          onChange={(e) => setCurrentSetting(prev => ({
                            ...prev,
                            enableDynamicFill: e.target.checked
                          }))}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          啟用動態填充
                        </label>
                      </div>
                    </>
                  ))}

                  <div className="flex justify-end space-x-3 mt-4">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      {editMode ? '更新' : '創建'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SeoManager; 